<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-xl bg-gray-800 text-gray-300 rounded-lg shadow-lg border border-gray-700 animate-scaleIn flex flex-col gap-4 relative" style="max-height:90vh;">
      <!-- Header -->
      <div class="flex items-center justify-between px-4 pt-4">
        <div class="flex items-center gap-3">
          <img
            :src="cardData?.img || defaultImg"
            alt="Card Image"
            class="w-9 h-9 sm:w-14 sm:h-14 rounded-md object-cover border-2"
            :class="cardData?.rarityColors?.borderColor || 'border-gray-600'"
            @error="handleImageError"
          />
          <div>
            <h1 class="text-base sm:text-lg font-semibold" :class="cardData?.rarityColors?.textColor || 'text-gray-300'">
              {{ cardData?.name || 'ไม่ระบุ' }}
            </h1>
            <p class="text-gray-300 text-xs sm:text-sm">ประเภท: {{ cardData?.type || 'ไม่ระบุ' }}</p>
            <div v-if="cardData?.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="star in Number(cardData.rarity)" :key="star">
                <i class="fas fa-star text-yellow-400 text-xs sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Notification -->
      <div v-if="notificationMessage" class="px-4 -mt-2">
        <div class="text-center text-xs sm:text-sm" :class="notificationType === 'success' ? 'text-green-400' : 'text-red-300'">
          {{ notificationMessage }}
        </div>
      </div>

      <!-- Content -->
      <div class="px-4 pb-4 space-y-4 overflow-y-auto scrollbar-thin" style="max-height: calc(90vh - 170px);">
        <!-- Compatible Cybernetic Organ Selection -->
        <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
          <h2 class="text-sm font-semibold text-gray-100 mb-2">เลือกอวัยวะจักรกลที่เข้ากันได้</h2>
          <div v-if="compatibleEquipment.length === 0" class="text-center text-yellow-400 text-xs sm:text-sm">
            ไม่พบอวัยวะจักรกลที่เข้ากันได้ในกระเป๋า
          </div>
          <div v-else class="space-y-2 max-h-32 overflow-y-auto scrollbar-thin">
            <div
              v-for="item in compatibleEquipment"
              :key="item.id"
              @click="selectEquipment(item.id)"
              class="flex items-center gap-2 bg-gray-700 p-2 rounded-md cursor-pointer hover:bg-gray-600 transition-colors"
              :class="{ 'border-indigo-500 bg-indigo-900': selectedEquipmentId === item.id }"
            >
              <img
                :src="item.img || defaultImg"
                class="w-6 h-6 rounded object-cover border"
                :class="item.rarityColors?.borderColor || 'border-gray-600'"
                @error="handleImageError"
              />
              <span class="text-gray-300 flex-1">
                {{ item.name }} <span v-if="item.level">+{{ item.level }}</span>
              </span>
              <input
                type="checkbox"
                :checked="selectedEquipmentId === item.id"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded pointer-events-none"
              />
            </div>
          </div>
        </section>

        <!-- Stats Preview -->
        <section v-if="selectedEquipment && activeStats.length > 0" class="bg-gray-800 border border-gray-700 rounded-md p-3">
          <h2 class="text-sm font-semibold text-gray-100 mb-2">สถานะที่ได้รับหลังติดตั้ง</h2>
          <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
            <div v-for="{ key, value } in activeStats" :key="key" class="flex items-center gap-2">
              <i :class="['text-indigo-400 text-sm', statIcons[key] || 'fas fa-star']"></i>
              <span class="text-gray-300 capitalize">
                {{ key }}: <strong class="text-gray-100">+{{ value }}</strong>
              </span>
            </div>
          </div>
        </section>

        <!-- Action Button -->
        <section>
          <button
            v-if="!isInstalling"
            @click="installCard"
            class="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-2 rounded-md transition-colors duration-200 border border-indigo-700 flex items-center justify-center gap-2"
            :disabled="!selectedEquipmentId"
          >
            <i class="fas fa-id-card text-xs"></i>
            <span>ติดตั้งการ์ด</span>
          </button>
          <div v-else class="flex items-center justify-center py-2">
            <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600"></div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFunctions } from '@/firebase/config';
import defaultImg from '@/assets/default-item.png';

export default {
  name: 'InstallCardModal',
  props: {
    itemId: {
      type: String,
      required: true,
    },
    inventoryItems: {
      type: Array,
      required: true,
    },
  },
  emits: ['close', 'card-installed'],
  setup(props, { emit }) {
    const cardData = ref({
      stats: {
        stamina: 0,
        str: 0,
        int: 0,
        agi: 0,
        luck: 0,
        maxHp: 0,
        atk: 0,
        evasion: 0,
        critical: 0,
        defense: 0,
      },
    });
    const selectedEquipmentId = ref(null);
    const isInstalling = ref(false);
    const notificationMessage = ref('');
    const notificationType = ref('success');

    const statIcons = {
      stamina: 'fas fa-heart',
      str: 'fas fa-dumbbell',
      int: 'fas fa-brain',
      agi: 'fas fa-running',
      luck: 'fas fa-dice',
      maxHp: 'fas fa-heartbeat',
      atk: 'fas fa-fist-raised',
      evasion: 'fas fa-running',
      critical: 'fas fa-bolt',
      defense: 'fas fa-shield-alt',
    };

    const statKeys = [
      'stamina',
      'str',
      'int',
      'agi',
      'luck',
      'maxHp',
      'atk',
      'evasion',
      'critical',
      'defense',
    ];

    // Computed property to filter non-zero stats
    const activeStats = computed(() => {
      if (!cardData.value.stats) return [];
      return statKeys
        .map(key => ({
          key,
          value: cardData.value.stats[key] || 0,
        }))
        .filter(stat => stat.value > 0);
    });

    // Filter for cybernetic organs that match the card's compatibleEquipment
    const compatibleEquipment = computed(() => {
      return props.inventoryItems.filter(item => 
        item.type === 'อวัยวะจักรกล' && 
        item.name === cardData.value.compatibleEquipment && 
        item.id !== props.itemId &&
        !item.locked &&
        !(item.cardSlots || []).some(slot => slot.cardName === cardData.value.name || slot.cardTemplateId === cardData.value.templateId)
      );
    });

    const selectedEquipment = computed(() => {
      return props.inventoryItems.find(item => item.id === selectedEquipmentId.value || item.instanceId === selectedEquipmentId.value);
    });

    const selectEquipment = (id) => {
      selectedEquipmentId.value = selectedEquipmentId.value === id ? null : id;
    };

    const handleImageError = (event) => {
      event.target.src = defaultImg;
    };

    const showNotification = (message, type = 'success') => {
      notificationMessage.value = message;
      notificationType.value = type;
      setTimeout(() => {
        notificationMessage.value = '';
      }, 3000);
    };

    const installCard = async () => {
      if (!selectedEquipmentId.value) {
        showNotification('กรุณาเลือกอวัยวะจักรกลที่ต้องการติดตั้งการ์ด', 'error');
        return;
      }
      isInstalling.value = true;
      try {
        const installCardFunction = projectFunctions.httpsCallable('gameActions');
        const result = await installCardFunction({
          action: 'mechanicalOrganAction',
          subAction: 'installCard',
          cardId: props.itemId,
          equipmentId: selectedEquipmentId.value,
        });
        const { success, message, updatedItem } = result.data;
        if (success) {
          showNotification('ติดตั้งการ์ดสำเร็จ!', 'success');
          emit('card-installed', updatedItem);
          emit('close');
        } else {
          showNotification(message || 'ติดตั้งการ์ดล้มเหลว', 'error');
        }
      } catch (err) {
        showNotification(err.message || 'เกิดข้อผิดพลาดในการติดตั้งการ์ด', 'error');
      } finally {
        isInstalling.value = false;
      }
    };

    const syncCardData = () => {
      const found = props.inventoryItems.find(item => item.id === props.itemId || item.instanceId === props.itemId);
      if (!found) {
        showNotification('ไม่พบข้อมูลการ์ด', 'error');
        emit('close');
        return;
      }
      cardData.value = {
        ...found,
        stats: {
          stamina: found.stats?.stamina || 0,
          str: found.stats?.str || 0,
          int: found.stats?.int || 0,
          agi: found.stats?.agi || 0,
          luck: found.stats?.luck || 0,
          maxHp: found.stats?.maxHp || 0,
          atk: found.stats?.atk || 0,
          evasion: found.stats?.evasion || 0,
          critical: found.stats?.critical || 0,
          defense: found.stats?.defense || 0,
        },
      };
    };

    onMounted(syncCardData);
    watch(() => props.inventoryItems, syncCardData, { deep: true });

    return {
      cardData,
      selectedEquipmentId,
      compatibleEquipment,
      selectedEquipment,
      isInstalling,
      notificationMessage,
      notificationType,
      statIcons,
      statKeys,
      activeStats,
      defaultImg,
      selectEquipment,
      handleImageError,
      showNotification,
      installCard,
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
</style>
